/**
 * GatewayFlow: High-Performance Interactive Bezier Flow & Shockwave Engine
 *
 * Visualizes dual-sided stream curves converging into the footer's singularity horizon:
 * 1. Symmetrical cubic Bezier paths originating from left and right edges
 * 2. Traveling luminous micro-particles with continuous loop progression
 * 3. Interactive click shockwaves: Deflects particles away via inverse-distance force physics
 * 4. Ambient pulse wave rendering for click ripples
 * 5. Throttled via IntersectionObserver: Automatically pauses RAF loop when out of viewport
 * 6. High-DPI / Retina canvas support with responsive recalculation
 *
 * Stack: Vanilla ES6 Module, HTML5 Canvas 2D
 * Architecture: Nirajan <https://nbk.com.np>
 */

export class GatewayFlow {
  /**
   * @param {string|HTMLCanvasElement} canvasTarget - Canvas element or ID
   * @param {Object} [options] - Configuration knobs
   */
  constructor(canvasTarget = 'footer-flow-canvas', options = {}) {
    this.canvas =
      typeof canvasTarget === 'string' ? document.getElementById(canvasTarget) : canvasTarget;
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    // Configurable options - elegant, subtle and transparent stream lines
    this.options = {
      numPaths: options.numPaths ?? 88,
      baseSpeed: options.baseSpeed ?? 1.0,
      strokeAlpha: options.strokeAlpha ?? 0.14, // Lower opacity / more transparent
      particleAlpha: options.particleAlpha ?? 0.65, // Gentle, soft particle glow
      lineWidth: options.lineWidth ?? 1.15,
      centerOffsetY: options.centerOffsetY ?? 0.5, // Center convergence
      ...options,
    };

    this.width = 0;
    this.height = 0;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.isRunning = false;
    this.isVisible = false;
    this.animFrameId = null;

    this.paths = [];
    this.explosions = [];
    this.mouse = { x: -9999, y: -9999, isHovering: false };

    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    this.handleResize = () => this.resize();
    window.addEventListener('resize', this.handleResize, { passive: true });

    // Track click & pointer interactions on the gateway flow stage
    this.stage =
      this.canvas.closest('.nbk-gateway-flow-stage') ||
      this.canvas.closest('footer') ||
      this.canvas.parentElement;
    if (this.stage) {
      this.handleClick = (e) => this.triggerExplosion(e);
      this.handlePointerMove = (e) => this.updatePointer(e);
      this.handlePointerLeave = () => {
        this.mouse.x = -9999;
        this.mouse.y = -9999;
        this.mouse.isHovering = false;
      };

      this.stage.addEventListener('click', this.handleClick, { passive: true });
      this.stage.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.stage.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
    }

    this.resize();

    // IntersectionObserver: Only render when footer is visible in the viewport
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      this.observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;
            if (this.isVisible && !this.isRunning) {
              this.start();
            } else if (!this.isVisible && this.isRunning) {
              this.stop();
            }
          });
        },
        { rootMargin: '100px' },
      );
      this.observer.observe(this.canvas);
    } else {
      this.start();
    }
  }

  resize() {
    this.stage =
      this.canvas.closest('.nbk-gateway-flow-stage') ||
      this.canvas.closest('footer') ||
      this.canvas.parentElement;
    const rect = (this.stage || this.canvas).getBoundingClientRect();
    this.width = Math.max(rect.width || window.innerWidth, 320);
    this.height = Math.max(rect.height || 480, 360);

    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(this.dpr, this.dpr);

    this.buildPaths();

    if (!this.isRunning && this.isVisible) {
      this.render();
    }
  }

  buildPaths() {
    this.paths = [];
    const count =
      this.width < 640 ? Math.round(this.options.numPaths * 0.6) : this.options.numPaths;
    const speedMultiplier = this.isReducedMotion ? 0.3 : 1.0;

    for (let i = 0; i < count; i++) {
      this.paths.push({
        isLeft: i % 2 === 0,
        // Broad vertical fan-out covering above and below viewport edges
        startY: (i / count) * this.height * 2.0 - this.height * 0.5,
        particles: [
          {
            t: Math.random(),
            speed: (0.0018 + Math.random() * 0.0026) * speedMultiplier * this.options.baseSpeed,
            size: 3.8 + Math.random() * 2.2, // Substantially bigger glowing nodes
          },
        ],
      });
    }
  }

  updatePointer(e) {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = e.clientX - rect.left;
    this.mouse.y = e.clientY - rect.top;
    this.mouse.isHovering = true;
  }

  triggerExplosion(e) {
    if (this.isReducedMotion) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Add large, expansive explosion shockwave
    this.explosions.push({
      x,
      y,
      radius: 0,
      maxRadius: 340,
      life: 1.0,
      decay: 0.016,
      speed: 17,
    });
  }

  /**
   * Evaluates cubic Bezier point at parameter t [0, 1]
   */
  getBezierPoint(t, p0, p1, p2, p3) {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;

    return {
      x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
      y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
    };
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.loop = () => {
      this.render();
      if (this.isRunning) {
        this.animFrameId = window.requestAnimationFrame(this.loop);
      }
    };
    this.animFrameId = window.requestAnimationFrame(this.loop);
  }

  stop() {
    this.isRunning = false;
    if (this.animFrameId) {
      window.cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
  }

  render() {
    const { ctx, width, height, paths, explosions, options } = this;
    ctx.clearRect(0, 0, width, height);

    const centerX = width / 2;
    const centerY = height * options.centerOffsetY;

    // 1. Update and render active shockwaves
    for (let j = explosions.length - 1; j >= 0; j--) {
      const exp = explosions[j];
      exp.radius += exp.speed;
      exp.life -= exp.decay;

      if (exp.life <= 0 || exp.radius >= exp.maxRadius) {
        explosions.splice(j, 1);
        continue;
      }

      // Draw expanding electric cyan & violet shockwave ripples
      ctx.beginPath();
      ctx.arc(exp.x, exp.y, exp.radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(103, 232, 249, ${(exp.life * 0.45).toFixed(3)})`;
      ctx.lineWidth = 2.0;
      ctx.stroke();

      // Outer cosmic indigo aura
      ctx.beginPath();
      ctx.arc(exp.x, exp.y, Math.max(0, exp.radius - 12), 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(129, 140, 248, ${(exp.life * 0.25).toFixed(3)})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }

    // 2. Render Bezier curves and moving particles
    const hasExplosions = explosions.length > 0;
    const strokeStyle = `rgba(255, 255, 255, ${options.strokeAlpha})`;

    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];

      // Define broad, sweeping Bezier trajectories converging into center horizon
      const p0 = { x: path.isLeft ? 0 : width, y: path.startY };
      const p1 = {
        x: path.isLeft ? centerX * 0.52 : width - centerX * 0.52,
        y: path.startY,
      };
      const p2 = {
        x: path.isLeft ? centerX * 0.82 : width - centerX * 0.82,
        y: centerY,
      };
      const p3 = { x: centerX, y: centerY };

      // Stream guide curve (bold dashed lines)
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);
      ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = options.lineWidth;
      ctx.setLineDash([1, 6]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Particles along path
      for (let pIdx = 0; pIdx < path.particles.length; pIdx++) {
        const p = path.particles[pIdx];
        p.t += p.speed;

        if (p.t > 1) {
          p.t = 0;
          path.startY += (Math.random() - 0.5) * 12;
        }

        const pos = this.getBezierPoint(p.t, p0, p1, p2, p3);

        // Apply explosion shockwave displacement force
        let dxTotal = 0;
        let dyTotal = 0;

        if (hasExplosions) {
          for (let eIdx = 0; eIdx < explosions.length; eIdx++) {
            const exp = explosions[eIdx];
            const dx = pos.x - exp.x;
            const dy = pos.y - exp.y;
            const dist = Math.hypot(dx, dy) || 1;
            const waveThickness = 140;

            if (dist < exp.radius + waveThickness && dist > exp.radius - waveThickness) {
              const force = (1 - Math.abs(dist - exp.radius) / waveThickness) * exp.life;
              dxTotal += (dx / dist) * force * 80;
              dyTotal += (dy / dist) * force * 80;
            }
          }
        }

        const renderX = pos.x + dxTotal;
        const renderY = pos.y + dyTotal;

        // Particle alpha fades gracefully near edges and center
        const edgeFade = Math.sin(p.t * Math.PI);
        const particleAlpha = (options.particleAlpha * edgeFade).toFixed(3);

        // Particle styling: enlarged luminous node
        const halfSize = p.size / 2;
        ctx.fillStyle = `rgba(255, 255, 255, ${particleAlpha})`;
        ctx.fillRect(renderX - halfSize, renderY - halfSize, p.size, p.size);
      }
    }
  }

  destroy() {
    this.stop();
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);

    if (this.stage) {
      this.stage.removeEventListener('click', this.handleClick);
      this.stage.removeEventListener('pointermove', this.handlePointerMove);
      this.stage.removeEventListener('pointerleave', this.handlePointerLeave);
    }
  }
}
